import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaProcedures, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaVideo,
  FaChartLine,
  FaMicroscope,
  FaHeartbeat,
  FaCalendarAlt,
  FaXRay
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ModuleCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
  
  h3 {
    color: #0a4d68;
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const FeatureBadge = styled.div`
  background: #f8fafc;
  border-radius: 30px;
  padding: 0.5rem 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem;
  
  svg {
    color: #ffd966;
  }
  
  span {
    color: #0a2b3c;
    font-weight: 500;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const InterventionalCoding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const modules = [
    {
      title: "Core Coding Principles",
      topics: [
        "ICD-10-PCS coding for interventional procedures",
        "CPT coding for radiology and vascular interventions",
        "Modifier usage in interventional radiology",
        "Bundled services and component coding"
      ]
    },
    {
      title: "Vascular Interventions",
      topics: [
        "Angioplasty and stenting procedures",
        "Atherectomy and thrombectomy",
        "Embolization and chemoembolization",
        "Venous access devices (ports, PICCs)"
      ]
    },
    {
      title: "Non-Vascular Interventions",
      topics: [
        "Biopsy procedures (core needle, fine needle)",
        "Drainage catheter placements",
        "Nephrostomy and biliary tube placements",
        "Radiofrequency and cryoablation"
      ]
    },
    {
      title: "Revenue Optimization",
      topics: [
        "E/M services in interventional radiology",
        "Documentation requirements",
        "Billing and reimbursement strategies",
        "Compliance and audit readiness"
      ]
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Interventional Radiology Coding
          <span>Master IR Coding & Revenue Optimization</span>
        </HeroTitle>
        <HeroDescription>
          SIR's new IR Coding Academy is your all-in-one resource for mastering interventional radiology 
          coding. This self-paced, expert-led training includes 15 on-demand videos covering core coding 
          principles, specialty-specific procedures, E/M services and revenue optimization strategies. [citation:4]
        </HeroDescription>
      </HeroSection>

      <Container>
        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Program Overview</SectionTitle>
            <p>
              The IR Coding Academy provides comprehensive training for interventional radiology coding 
              professionals. With annual updates—including 2025 changes—and new specialty content coming 
              regularly, you'll stay current with the latest coding guidelines and reimbursement strategies. [citation:4]
            </p>
            <p>
              This self-paced, expert-led training includes 15 on-demand videos covering core coding 
              principles, specialty-specific procedures, E/M services, and revenue optimization strategies. 
              Perfect for coders, billers, and revenue cycle professionals. [citation:4]
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1581093458791-9a3c3a2c3e4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="Interventional Radiology"
            />
          </ImageWrapper>
        </Grid2Col>

        <SectionTitle>Course Modules</SectionTitle>
        <Grid3Col>
          {modules.map((module, index) => (
            <ModuleCard key={index}>
              <h3>{module.title}</h3>
              <List>
                {module.topics.map((topic, idx) => (
                  <ListItem key={idx}>
                    <FaCheckCircle /> {topic}
                  </ListItem>
                ))}
              </List>
            </ModuleCard>
          ))}
        </Grid3Col>

        <SectionTitle>Annual Updates Included</SectionTitle>
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <FeatureBadge>
            <FaStar /> <span>2025 Code Changes</span>
          </FeatureBadge>
          <FeatureBadge>
            <FaStar /> <span>New Specialty Content 2025-2026</span>
          </FeatureBadge>
          <FeatureBadge>
            <FaStar /> <span>15 On-Demand Videos</span>
          </FeatureBadge>
          <FeatureBadge>
            <FaStar /> <span>Expert-Led Training</span>
          </FeatureBadge>
        </div>

        <CTASection>
          <h3>Ready to Master IR Coding?</h3>
          <p>Join the IR Coding Academy and become an expert in interventional radiology coding</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default InterventionalCoding;